import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, ReactNode } from 'react';

import { cn } from '../../libs/utils';
import { semanticColor } from '../../theme/semanticColor';
import { IndeterminateProgress } from '../progress';

import { BaseButton, BaseButtonProps } from './BaseButton';

export const textButtonVariants = cva('inline-flex items-center justify-center whitespace-nowrap transition-colors disabled:pointer-events-none', {
  variants: {
    variant: {
      secondary: 'text-secondary-foreground-main hover:bg-gradient-to-r from-secondary-background-hovered&pressed to-secondary-background-hovered&pressed',
      primary: 'text-primary-foreground-main hover:bg-gradient-to-r from-primary-background-hovered&pressed to-primary-background-hovered&pressed',
      error: 'text-error-foreground-main hover:bg-gradient-to-r from-error-background-hovered&pressed to-error-background-hovered&pressed',
      inverse: 'text-neutral-foreground-inverse-main hover:bg-neutral-background-inverse-hovered&pressed'
    },
    size: {
      large: 'rounded-bzc-medium px-normal-xlarge py-normal-medium label-large-semibold',
      medium: 'rounded-bzc-small px-normal-large py-normal-small label-medium-semibold',
      small: 'py-tiny-large px-normal-small rounded-bzc-small label-small-medium',
      xsmall: 'py-tiny-medium px-normal-small rounded-bzc-xsmall label-xsmall-medium'
    }
  },
  defaultVariants: {
    variant: 'primary',
    size: 'medium'
  }
});

const indeterminateProgressVariants = cva('absolute left-0 right-0 flex items-center justify-center gap-tiny-large text-neutral-foreground-disabled', {
  variants: {
    variant: {
      secondary: 'text-neutral-foreground-disabled',
      primary: 'text-neutral-foreground-disabled',
      error: 'text-neutral-foreground-disabled',
      inverse: 'text-neutral-foreground-inverse-disabled'
    },
    size: {
      large: 'gap-tiny-large',
      medium: 'gap-tiny-large',
      small: 'gap-tiny-medium',
      xsmall: 'gap-tiny-medium'
    }
  },
  defaultVariants: {
    size: 'medium'
  }
});

const LOADING_COLOR = {
  secondary: semanticColor.secondary.foreground.main,
  primary: semanticColor.primary.foreground.high,
  error: semanticColor.error.foreground.high,
  inverse: semanticColor.neutral.foreground['inverse-main']
};

const LOADING_INDETERMINATE_PROGRESS_SIZE = {
  large: 20,
  medium: 18,
  small: 16,
  xsmall: 14
};

export interface TextButtonProps extends Omit<BaseButtonProps, 'loadingColor'>, VariantProps<typeof textButtonVariants> {
  loadingText?: ReactNode;
}

export const TextButton = forwardRef<HTMLButtonElement, TextButtonProps>(({ className, variant, size, children, loadingText, ...props }, ref) => {
  const { disabled, isLoading } = props;

  return (
    <BaseButton
      className={cn(textButtonVariants({ variant, size, className }), {
        'text-neutral-foreground-disabled': disabled || isLoading
      })}
      loadingColor={LOADING_COLOR[variant as keyof typeof LOADING_COLOR]}
      ref={ref}
      {...props}
    >
      {isLoading && (
        <div className={indeterminateProgressVariants({ size, variant })}>
          <IndeterminateProgress
            variant={'circular'}
            color={variant !== 'inverse' ? 'dimmed' : 'dimmed-inverse'}
            size={size ? LOADING_INDETERMINATE_PROGRESS_SIZE[size] : 18}
          />
          {loadingText}
        </div>
      )}
      {children}
    </BaseButton>
  );
});

TextButton.displayName = 'BaseButton';
